import { useState, type FormEvent } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import {
  useGetMetaAgencyCredentialStatusQuery,
  useLazyGetMetaAgencyCredentialOauthUrlQuery,
  useRemoveMetaAgencyCredentialMutation,
  useSetMetaAgencyCredentialMutation,
} from "~/ui-rtk/api/partnershipsApi"
import type { MetaAgencyCredentialStatusDto } from "~/ui-rtk/api/types/partnerships.types"
// Direct imports (not the `common` barrel) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Input from "~/ui-rtk/components/ui/controls/Input"
import { META_ACCESS_MANAGE_URL } from "~/ui-rtk/components/features/settings/MetaAccessSettings/copy"
import { markAgencyLoginStarted } from "~/ui-rtk/utils/agency-login-marker"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import {
  AGENCY_CREDENTIAL_CONNECT_LABEL,
  AGENCY_CREDENTIAL_HELP,
  AGENCY_CREDENTIAL_INPUT_LABEL,
  AGENCY_CREDENTIAL_META_ACCESS_LINK,
  AGENCY_CREDENTIAL_OAUTH_HELP,
  AGENCY_CREDENTIAL_PASTE_PLACEHOLDER,
  AGENCY_CREDENTIAL_PASTE_TOGGLE,
  AGENCY_CREDENTIAL_REMOVE_CONFIRM,
  AGENCY_CREDENTIAL_TITLE,
} from "./partnership-copy"
import { StatusChip } from "./PartnershipsTable"

/**
 * Super-admin dialog for the single agency discovery credential (MD-5501):
 * the Marathon login content discovery runs on for brands whose connector
 * holds a system user. Shows the API's status (who, which business, scope
 * coverage, grant size, last verification) and takes a replacement two ways:
 *
 * - "Connect with Facebook" (primary): fetches Meta's login dialog URL from
 *   the API — its `state` carries a single-use nonce the API minted — and
 *   leaves for it. Meta sends the browser back to `/authenticate-connector`,
 *   which posts the code to the API; the token is exchanged and stored
 *   server-side and never reaches the browser.
 * - "Or paste a token" (fallback, behind a disclosure): the write-only
 *   password field. The value lives in component state only until the
 *   request resolves and is never rendered.
 *
 * The dialog is mounted only while open, so nothing survives a close. A
 * credential set either way reads the same in the status block.
 */

/** Mirrors the API DTO's `@MinLength(32)`. */
export const MIN_AGENCY_CREDENTIAL_LENGTH = 32

const DIALOG_CONTAINER_CLS =
  "flex max-h-[90vh] w-full max-w-[95vw] flex-col gap-3 rounded-lg border border-basic-blue bg-basic-dark-blue p-5 text-left text-xs text-gray-300 shadow-xl overflow-y-auto"

/** `aria-controls` target of the paste-a-token disclosure. */
const PASTE_SECTION_ID = "agency-credential-paste"

export interface AgencyCredentialDialogProps {
  onClose: () => void
  /** `settings.discoveryCredential.coversBrand` for the brand on screen. */
  coversBrand: boolean | null
  brandIgUsername: string | null
}

/** 'YYYY-MM-DD' from an ISO timestamp — enough for "set on / verified on". */
function isoDay(iso: string): string {
  return iso.slice(0, 10)
}

/** Warn this long before the login or its data access runs out. */
const EXPIRY_WARNING_MS = 14 * 24 * 60 * 60 * 1000

/** Amber chip text when `iso` is past or within 14 days, else null. */
function expiryWarningOf(iso: string): string | null {
  const left = new Date(iso).getTime() - Date.now()
  if (left <= 0) return "Expired — connect again"
  return left <= EXPIRY_WARNING_MS ? "Expires soon — connect again" : null
}

function ExpiryChip({ iso }: { iso: string }) {
  const warning = expiryWarningOf(iso)
  return warning ? <StatusChip tone="amber">{warning}</StatusChip> : null
}

function count(n: number, noun: string, plural = `${noun}s`): string {
  return `${n} ${n === 1 ? noun : plural}`
}

/**
 * "1 business · 2 Instagram accounts · 1 Page", "all Instagram accounts" for
 * an unrestricted kind, or the all-assets / unknown wording. A grant stored
 * before the per-kind flags existed has none of them.
 */
function grantSummaryOf(grant: MetaAgencyCredentialStatusDto["grant"]): string {
  if (!grant) return "unknown"
  const kinds = [
    grant.allBusinesses,
    grant.allInstagramAccounts,
    grant.allPages,
  ]
  const hasKindFlags = kinds.some(flag => flag !== undefined)
  if (hasKindFlags ? kinds.every(Boolean) : grant.allAssets) {
    return "all assets the login can see"
  }
  const part = (
    all: boolean | undefined,
    n: number,
    noun: string,
    plural: string,
  ) => (all ? `all ${plural}` : count(n, noun, plural))
  return [
    part(
      grant.allBusinesses,
      grant.businesses.length,
      "business",
      "businesses",
    ),
    part(
      grant.allInstagramAccounts,
      grant.instagramAccounts.length,
      "Instagram account",
      "Instagram accounts",
    ),
    part(grant.allPages, grant.pages.length, "Page", "Pages"),
  ].join(" · ")
}

function businessLabelOf(status: MetaAgencyCredentialStatusDto): string {
  if (!status.businessId) return "not a member of the agency business"
  return status.businessName
    ? `${status.businessName} · ${status.businessId}`
    : status.businessId
}

interface StatusSummaryProps {
  status: MetaAgencyCredentialStatusDto
  coversBrand: boolean | null
  brandIgUsername: string | null
}
function StatusSummary({
  status,
  coversBrand,
  brandIgUsername,
}: StatusSummaryProps) {
  if (!status.configured) {
    return (
      <p data-testid="agency-credential-status">
        No agency login is set. Brands connected with a system user skip content
        discovery until one is.
      </p>
    )
  }
  const brand = brandIgUsername
    ? `@${brandIgUsername}`
    : "this brand's Instagram account"
  // Coverage is as of the grant's last read from Meta: when the login was set,
  // or a later brand reconnect by the same Meta person that refreshed it.
  // Grants stored before readAt was recorded fall back to the login's own dates.
  const grantReadAt = status.grant?.readAt ?? status.verifiedAt ?? status.setAt
  return (
    <dl
      data-testid="agency-credential-status"
      className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1"
    >
      <dt className="text-system-gray-400">Connected as</dt>
      <dd className="text-white">
        {status.connectedUser
          ? `${status.connectedUser.name} · ${status.connectedUser.id}`
          : "unknown"}
      </dd>
      <dt className="text-system-gray-400">Business</dt>
      <dd className="text-white">{businessLabelOf(status)}</dd>
      <dt className="text-system-gray-400">Permissions</dt>
      <dd className="flex flex-wrap items-center gap-2">
        <span>{count(status.scopes.granted.length, "permission")} granted</span>
        {status.scopes.missingRequired.length > 0 ? (
          <StatusChip tone="amber">
            Missing: {status.scopes.missingRequired.join(", ")}
          </StatusChip>
        ) : (
          <StatusChip tone="green">All required present</StatusChip>
        )}
      </dd>
      <dt className="text-system-gray-400">App grant</dt>
      <dd className="flex flex-wrap items-center gap-2">
        <span>{grantSummaryOf(status.grant)}</span>
        {coversBrand === true && (
          <StatusChip tone="green">Covers {brand}</StatusChip>
        )}
        {coversBrand === false && (
          <StatusChip
            tone="amber"
            title={`Set the credential again from a login whose Meta dialog ticks ${brand}`}
          >
            Does not cover {brand}
          </StatusChip>
        )}
        {coversBrand !== null && grantReadAt && (
          <span className="text-system-gray-300">
            as of {isoDay(grantReadAt)}
          </span>
        )}
      </dd>
      {status.uncoveredBrands.length > 0 && (
        <>
          <dt className="text-system-gray-400">Not covered</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {status.uncoveredBrands.map(({ companyName, igUserId }) => (
              <StatusChip
                key={igUserId}
                tone="amber"
                title="A system-user brand whose Instagram account this login's Meta grant does not tick; discovery fails for it"
              >
                {`${companyName} (${igUserId})`}
              </StatusChip>
            ))}
          </dd>
        </>
      )}
      <dt className="text-system-gray-400">Verification</dt>
      <dd className="flex flex-wrap items-center gap-2">
        {status.verification ? (
          <>
            <StatusChip tone={status.verification.ok ? "green" : "amber"}>
              {status.verification.ok ? "Verified" : "Check failed"}
            </StatusChip>
            {status.verifiedAt && <span>on {isoDay(status.verifiedAt)}</span>}
            {status.verification.detail && (
              <span className="basis-full text-system-gray-300">
                {status.verification.detail}
              </span>
            )}
          </>
        ) : (
          <span>not verified yet</span>
        )}
      </dd>
      <dt className="text-system-gray-400">Set</dt>
      <dd>
        {status.setAt ? `on ${isoDay(status.setAt)}` : "unknown"}
        {status.setByName ? ` by ${status.setByName}` : ""}
      </dd>
      <dt className="text-system-gray-400">Expires</dt>
      <dd className="flex flex-wrap items-center gap-2">
        {status.expiresAt ? (
          <>
            <span>on {isoDay(status.expiresAt)}</span>
            <ExpiryChip iso={status.expiresAt} />
          </>
        ) : (
          <span>no expiry reported</span>
        )}
      </dd>
      {status.dataAccessExpiresAt && (
        <>
          <dt className="text-system-gray-400">Data access</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <span>until {isoDay(status.dataAccessExpiresAt)}</span>
            <ExpiryChip iso={status.dataAccessExpiresAt} />
          </dd>
        </>
      )}
      {status.lastError && (
        <>
          <dt className="text-system-gray-400">Last problem</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <StatusChip tone="amber">Needs attention</StatusChip>
            {status.lastCheckedAt && (
              <span>on {isoDay(status.lastCheckedAt)}</span>
            )}
            <span className="basis-full text-system-gray-300">
              {status.lastError}
            </span>
          </dd>
        </>
      )}
    </dl>
  )
}

export default function AgencyCredentialDialog({
  onClose,
  coversBrand,
  brandIgUsername,
}: AgencyCredentialDialogProps) {
  const {
    data: status,
    isLoading,
    isError,
  } = useGetMetaAgencyCredentialStatusQuery()
  const [getOauthUrl, { isFetching: fetchingOauthUrl }] =
    useLazyGetMetaAgencyCredentialOauthUrlQuery()
  const [setCredential, { isLoading: saving }] =
    useSetMetaAgencyCredentialMutation()
  const [removeCredential, { isLoading: removing }] =
    useRemoveMetaAgencyCredentialMutation()

  const [value, setValue] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [pasteOpen, setPasteOpen] = useState(false)
  // Set once the browser has been told to leave for Meta and never cleared:
  // the navigation is in flight, so nothing here may fire again.
  const [leaving, setLeaving] = useState(false)

  const connecting = fetchingOauthUrl || leaving
  const busy = saving || removing || connecting
  const canSubmit = value.trim().length >= MIN_AGENCY_CREDENTIAL_LENGTH && !busy
  const configured = status?.configured === true

  const close = () => {
    setValue("")
    setError(null)
    onClose()
  }

  const connectWithFacebook = async () => {
    setError(null)
    try {
      // Fetched on every click: the URL's `state` is single use.
      const { url } = await getOauthUrl().unwrap()
      setLeaving(true)
      markAgencyLoginStarted()
      // Full-page navigation into Meta's dialog; it sends the browser back
      // to /authenticate-connector, which finishes the exchange.
      window.location.assign(url)
    } catch (err) {
      setError(
        getServerErrorMessage(
          err,
          "Couldn't start the Facebook login. Try again.",
        ),
      )
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setError(null)
    try {
      const saved = await setCredential({
        agencyAccessToken: value.trim(),
      }).unwrap()
      setValue("")
      toast.success(
        saved.verification && !saved.verification.ok
          ? "Agency discovery credential saved, but its check failed — see the dialog."
          : "Agency discovery credential saved.",
      )
    } catch (err) {
      // The API's message is the user-facing verdict (wrong app, a system
      // user, missing scopes) — show it verbatim.
      setError(
        getServerErrorMessage(
          err,
          "Couldn't save the agency discovery credential. Try again.",
        ),
      )
    }
  }

  const remove = async () => {
    setError(null)
    try {
      await removeCredential().unwrap()
      setConfirmRemove(false)
      toast.success("Agency discovery credential removed.")
    } catch (err) {
      setError(
        getServerErrorMessage(
          err,
          "Couldn't remove the agency discovery credential. Try again.",
        ),
      )
    }
  }

  return (
    <Dialog
      isOpen
      handleClose={close}
      closeOnClickOutside
      width={560}
      classes={{ container: DIALOG_CONTAINER_CLS }}
    >
      <div>
        <h2 className="text-lg font-semibold text-white">
          {AGENCY_CREDENTIAL_TITLE}
        </h2>
        <Link
          to={META_ACCESS_MANAGE_URL}
          className="text-system-sea-500 hover:underline"
        >
          {AGENCY_CREDENTIAL_META_ACCESS_LINK}
        </Link>
        <p className="mt-1 text-gray-400">{AGENCY_CREDENTIAL_HELP}</p>
      </div>

      {isLoading && <p>Loading…</p>}
      {isError && (
        <p className="text-states-error">
          Couldn&apos;t load the agency credential status.
        </p>
      )}
      {status && (
        <StatusSummary
          status={status}
          coversBrand={coversBrand}
          brandIgUsername={brandIgUsername}
        />
      )}

      <div className="flex flex-col gap-2 rounded-md border border-basic-blue px-system-md py-system-sm">
        <p className="text-gray-300">{AGENCY_CREDENTIAL_OAUTH_HELP}</p>
        <div>
          <button
            type="button"
            onClick={() => void connectWithFacebook()}
            disabled={busy}
            className="btn btn-primary btn-sm"
          >
            {connecting && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {AGENCY_CREDENTIAL_CONNECT_LABEL}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setPasteOpen(open => !open)}
          aria-expanded={pasteOpen}
          aria-controls={PASTE_SECTION_ID}
          className="btn btn-ghost btn-xs self-start"
        >
          {AGENCY_CREDENTIAL_PASTE_TOGGLE}
        </button>
        {pasteOpen && (
          <form
            id={PASTE_SECTION_ID}
            onSubmit={event => void submit(event)}
            onKeyDown={({ key }) => {
              if (key === "Escape") close()
            }}
            className="flex flex-col gap-2"
          >
            <div className="flex flex-col gap-1">
              <span>{AGENCY_CREDENTIAL_INPUT_LABEL}</span>
              <Input
                type="password"
                fullWidth
                autoComplete="off"
                spellCheck={false}
                aria-label={AGENCY_CREDENTIAL_INPUT_LABEL}
                placeholder={AGENCY_CREDENTIAL_PASTE_PLACEHOLDER}
                value={value}
                onChange={({ target }) => setValue(target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!canSubmit}
                className="btn btn-outline btn-sm"
              >
                {saving && (
                  <span className="loading loading-spinner loading-xs" />
                )}
                {configured ? "Replace" : "Set"}
              </button>
            </div>
          </form>
        )}
      </div>

      {error && (
        <p role="alert" className="text-states-error">
          {error}
        </p>
      )}

      <div className="mt-1 flex flex-wrap items-center justify-end gap-2">
        {configured && !confirmRemove && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirmRemove(true)}
            className="btn btn-ghost btn-sm mr-auto text-system-red-300"
          >
            Remove
          </button>
        )}
        <button
          type="button"
          onClick={close}
          disabled={busy}
          className="btn btn-ghost btn-sm"
        >
          Close
        </button>
      </div>

      {confirmRemove && (
        <div
          role="alertdialog"
          aria-label="Confirm removal"
          className="flex flex-col gap-2 rounded-md border border-system-red-600 bg-system-red-900/30 px-system-md py-system-sm text-system-red-100"
        >
          <span>{AGENCY_CREDENTIAL_REMOVE_CONFIRM}</span>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmRemove(false)}
              disabled={busy}
              className="btn btn-ghost btn-xs"
            >
              Keep it
            </button>
            <button
              type="button"
              onClick={() => void remove()}
              disabled={busy}
              className="btn btn-error btn-xs"
            >
              {removing && (
                <span className="loading loading-spinner loading-xs" />
              )}
              Remove login
            </button>
          </div>
        </div>
      )}
    </Dialog>
  )
}
